import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import * as state from '../../../../../../../state'
import * as modals from '../../modals'

/**
 * Component for displaying the user access page, which shows who can work on
 * the current survey. The user picks between the survey admins and the standard
 * users from a dropdown, which swaps the table below it for the collection
 * backing that list.
 */
@ngCore.Component({
  imports: [
    ds.illustration.emptyState.Component,
    ds.input.select.single.Component,
    ds.layout.page.Component,
    ds.layout.page.child.header.Component,
    ds.loader.Component,
    ds.overlay.confirmation.Component,
    ds.table.Component,
    modals.addStandardUser.Component,
    modals.addSurveyAdmin.Component,
  ],
  selector: 'atn-space-lite-user-access',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.userAccess.surveyAdmins.Collection} */
  public readonly surveyAdminsEc = ngCore.inject(
    state.space.userAccess.surveyAdmins.Collection,
  )

  /** @see {@link state.space.userAccess.standardUsers.Collection} */
  public readonly standardUsersEc = ngCore.inject(
    state.space.userAccess.standardUsers.Collection,
  )

  /** @see {@link state.me.Feature} */
  private readonly _meFeature = ngCore.inject(state.me.Feature)

  /**
   * Whether the current user can access the page at all: platform admins,
   * tenant admins, and survey admins. Everyone else is shown an unauthorized
   * message instead.
   */
  public readonly isAdminSg = ngCore.computed(
    (
      canManageUsers = this._meFeature.contexts.selectors.currentUserCanManageUsers.asSignal(),
      currentUserId = this._meFeature.selectors.currentUserId.asSignal(),
      surveyAdminUserIds = new Set(
        this.surveyAdminsEc.plugins.currentListPlugin.feature.selectors.entities
          .asSignal()
          .map(({ userId }) => userId),
      ),
    ) =>
      canManageUsers ||
      (!!currentUserId && surveyAdminUserIds.has(currentUserId)),
  )

  /**
   * The items to display in the user access type dropdown, each naming the
   * users of that type. For example, "Survey Admin" is offered as
   * "Survey Admins".
   */
  public readonly userAccessTypeSelectItems: readonly ds.input.select.models.Item<ents.extra.space.User.Access.Type>[] =
    ents.extra.space.User.Access.Type.ALL.map(userAccessType => ({
      // TODO: Handle pluralization properly
      label: `${userAccessType}s`,
      value: userAccessType,
    }))

  /**
   * The user access type the page currently presents
   *
   * @default ents.extra.space.User.Access.Type.SurveyAdmin
   */
  public readonly selectedUserAccessTypeSg =
    ngCore.signal<ents.extra.space.User.Access.Type>(
      ents.extra.space.User.Access.Type.SurveyAdmin,
    )

  /**
   * The name of the table to display, derived from the selected user access
   * type. For example, if "Survey Admin" is selected, this would return
   * "Survey Admins".
   */
  public readonly tableNameSg = ngCore.computed(
    (selectedUserAccessType = this.selectedUserAccessTypeSg()) =>
      // TODO: Handle pluralization properly
      `${selectedUserAccessType}s`,
  )

  /** Current type-scoped user access collection */
  public readonly userAccessEcSg = ngCore.computed<
    ec.Collection<state.space.userAccess.TypeParams>
  >((selectedUserAccessType = this.selectedUserAccessTypeSg()) =>
    selectedUserAccessType === ents.extra.space.User.Access.Type.SurveyAdmin
      ? this.surveyAdminsEc
      : this.standardUsersEc,
  )

  /** Canonical table input for the selected lite user-access collection. */
  public readonly tableConfigSg = ngCore.computed(
    (
      collection = this.userAccessEcSg(),
      canAdd = this.canAddUserAccessSg(),
      title = this.tableNameSg(),
    ) =>
      ({
        collection,
        view: {
          creation: canAdd,
          showColumnSettings: false,
          title,
        },
      }) satisfies ds.table.ec.Config.Input<state.space.userAccess.TypeParams>,
  )

  /**
   * Whether the presented list can be added to.
   * Platform admins, tenant admins, and survey admins can assign standard users,
   * while only platform admins and tenant admins manage survey admins.
   */
  public readonly canAddUserAccessSg = ngCore.computed(
    (
      selectedUserAccessType = this.selectedUserAccessTypeSg(),
      canManageUsers = this._meFeature.contexts.selectors.currentUserCanManageUsers.asSignal(),
      isAdmin = this.isAdminSg(),
    ) =>
      selectedUserAccessType === ents.extra.space.User.Access.Type.StandardUser
        ? isAdmin
        : canManageUsers,
  )

  /**
   * Switches the page between the survey admins and the standard users.
   */
  public selectedUserAccessTypeChangedHandler(
    selectedUserAccessType: ents.extra.space.User.Access.Type | undefined,
  ): void {
    this.selectedUserAccessTypeSg.set(
      selectedUserAccessType ?? ents.extra.space.User.Access.Type.SurveyAdmin,
    )
  }

  /** Confirms the deletion of the assignment marked for removal */
  public confirmDeletionClickHandler(): void {
    this.userAccessEcSg()
      .plugins.editingPlugin.feature.actions.submit.from(
        'UserAccess.Delete.Confirmation.Confirm.Clicked',
      )
      .dispatch({ delete: true })
  }

  /** Cancels the deletion of the assignment marked for removal */
  public cancelDeletionClickHandler(): void {
    this.userAccessEcSg()
      .plugins.editingPlugin.feature.actions.discard.from(
        'UserAccess.Delete.Confirmation.Cancel.Clicked',
      )
      .dispatch({ delete: true })
  }
}
